import {
  Table, Badge, Group, Button, Modal, TextInput, Textarea, ActionIcon, Tooltip,
  Alert, Skeleton, Text, Stack, MultiSelect, Select, ScrollArea, Code,
  SegmentedControl, Switch,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'
import {
  IconPlus, IconTrash, IconKey, IconAlertTriangle, IconEye, IconCopy,
} from '@tabler/icons-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import {
  ldapApi, isReleituraFalhou,
  type LdapOAuth2Client, type LdapOAuth2ReleituraFalhou,
} from '@/api/ldap'
import { formatApiError } from '@/api/errors'

const ESCOPOS_OPTIONS_SEM_OPENID = ['profile', 'email', 'groups', 'address', 'phone', 'offline_access']

/** Códigos crus que a API devolve em `detail` (string) para casos esperados — mapear
 * para PT-BR antes de cair no `formatApiError` genérico. */
const CODIGO_ERRO_PT: Record<string, string> = {
  cliente_oauth2_inexistente: 'Cliente OAuth2 não existe (pode ter sido removido)',
  admin_required: 'Só administrador do LDAP pode fazer isto',
  kanidm_not_configured: 'Kanidm não está configurado neste ambiente',
}

type ListaOAuth2 = { items: LdapOAuth2Client[]; count: number }
type ResultadoMutacao = LdapOAuth2Client | LdapOAuth2ReleituraFalhou
type TipoCliente = 'confidencial' | 'publico'

function parseRedirects(texto: string): string[] {
  return texto
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l.length > 0)
}

export function ClientesOAuth2Tab() {
  const qc = useQueryClient()

  const [createOpened, { open: openCreate, close: closeCreateBase }] = useDisclosure(false)
  const [newName, setNewName] = useState('')
  const [newDisplayname, setNewDisplayname] = useState('')
  const [newLanding, setNewLanding] = useState('')
  const [newRedirects, setNewRedirects] = useState('')
  const [newTipo, setNewTipo] = useState<TipoCliente>('confidencial')
  const [newLocalhost, setNewLocalhost] = useState(false)

  const [detailName, setDetailName] = useState<string | null>(null)
  const [novoRedirect, setNovoRedirect] = useState('')
  const [confirmarUltimoRedirect, setConfirmarUltimoRedirect] =
    useState<{ name: string; url: string } | null>(null)
  const [novoScopeMapGrupo, setNovoScopeMapGrupo] = useState<string | null>(null)
  const [novoScopeMapEscopos, setNovoScopeMapEscopos] = useState<string[]>(['openid'])

  const [deleteAlvo, setDeleteAlvo] = useState<LdapOAuth2Client | null>(null)
  const [deleteValor, setDeleteValor] = useState('')

  const [revelarAberto, setRevelarAberto] = useState(false)
  const [revelarNome, setRevelarNome] = useState<string | null>(null)
  const [segredo, setSegredo] = useState<string | null>(null)
  const [segredoVisivel, setSegredoVisivel] = useState(false)

  const { data, isLoading, error } = useQuery({
    queryKey: ['ldap-oauth2'],
    queryFn: () => ldapApi.listOAuth2(),
  })

  const { data: groupsData } = useQuery({
    queryKey: ['ldap-groups'],
    queryFn: () => ldapApi.listGroups(false),
  })

  function closeCreate() {
    closeCreateBase()
    setNewName('')
    setNewDisplayname('')
    setNewLanding('')
    setNewRedirects('')
    setNewTipo('confidencial')
    setNewLocalhost(false)
  }

  function closeDetail() {
    setDetailName(null)
    setNovoRedirect('')
    setConfirmarUltimoRedirect(null)
    setNovoScopeMapGrupo(null)
    setNovoScopeMapEscopos(['openid'])
  }

  function closeDelete() {
    setDeleteAlvo(null)
    setDeleteValor('')
  }

  function closeRevelar() {
    setRevelarAberto(false)
    setRevelarNome(null)
    setSegredo(null)
    setSegredoVisivel(false)
  }

  /** Upsert local do RS retornado por uma mutação bem-sucedida, ou — quando o Kanidm
   * aceitou a escrita mas a releitura falhou — aviso + reinvalidação (a trilha já
   * registrou sucesso; a lista nunca renderiza esse corpo como cliente). */
  function aplicarResultado(resultado: ResultadoMutacao) {
    if (isReleituraFalhou(resultado)) {
      notifications.show({
        color: 'yellow',
        title: 'Alteração aplicada',
        message: `A alteração em ${resultado.name} foi aplicada no Kanidm, mas a lista não `
          + 'pôde ser recarregada automaticamente. Atualizando a lista...',
      })
      qc.invalidateQueries({ queryKey: ['ldap-oauth2'] })
      return
    }
    qc.setQueryData<ListaOAuth2 | undefined>(['ldap-oauth2'], (old) => {
      if (!old) return old
      const existe = old.items.some((i) => i.name === resultado.name)
      const items = existe
        ? old.items.map((i) => (i.name === resultado.name ? resultado : i))
        : [...old.items, resultado].sort((a, b) => a.name.localeCompare(b.name))
      return { ...old, items, count: items.length }
    })
  }

  function onErro(fallback: string) {
    return (err: unknown) => {
      const codigo = (err as { response?: { data?: { detail?: unknown } } })
        ?.response?.data?.detail
      const message = typeof codigo === 'string' && codigo in CODIGO_ERRO_PT
        ? CODIGO_ERRO_PT[codigo]
        : formatApiError(err, fallback)
      notifications.show({ color: 'red', title: 'Erro', message })
    }
  }

  const createMutation = useMutation({
    mutationFn: () => {
      const dados = {
        name: newName,
        displayname: newDisplayname,
        landing_url: newLanding,
        redirects: parseRedirects(newRedirects),
      }
      // Público vai por rota própria; a flag não existe no confidencial (spec §7.2).
      return newTipo === 'publico'
        ? ldapApi.createOAuth2Public({ ...dados, localhost_redirect: newLocalhost })
        : ldapApi.createOAuth2(dados)
    },
    onSuccess: (resultado) => {
      aplicarResultado(resultado)
      closeCreate()
      notifications.show({ color: 'teal', title: 'Cliente criado', message: `${newName} criado com sucesso.` })
    },
    onError: onErro('Falha ao criar cliente.'),
  })

  const addRedirectMutation = useMutation({
    mutationFn: ({ name, url }: { name: string; url: string }) => ldapApi.addOAuth2Redirect(name, url),
    onSuccess: (resultado) => {
      aplicarResultado(resultado)
      setNovoRedirect('')
    },
    onError: onErro('Falha ao adicionar redirect.'),
  })

  const removeRedirectMutation = useMutation({
    mutationFn: ({ name, url }: { name: string; url: string }) => ldapApi.removeOAuth2Redirect(name, url),
    onSuccess: (resultado) => aplicarResultado(resultado),
    onError: onErro('Falha ao remover redirect.'),
  })

  const setScopeMapMutation = useMutation({
    mutationFn: ({ name, group, scopes }: { name: string; group: string; scopes: string[] }) =>
      ldapApi.setOAuth2ScopeMap(name, group, scopes),
    onSuccess: (resultado) => {
      aplicarResultado(resultado)
      setNovoScopeMapGrupo(null)
      setNovoScopeMapEscopos(['openid'])
    },
    onError: onErro('Falha ao definir scope map.'),
  })

  const removeScopeMapMutation = useMutation({
    mutationFn: ({ name, group }: { name: string; group: string }) => ldapApi.removeOAuth2ScopeMap(name, group),
    onSuccess: (resultado) => aplicarResultado(resultado),
    onError: onErro('Falha ao remover scope map.'),
  })

  const localhostMutation = useMutation({
    mutationFn: ({ name, enabled }: { name: string; enabled: boolean }) =>
      ldapApi.setOAuth2LocalhostRedirect(name, enabled),
    onSuccess: (resultado) => aplicarResultado(resultado),
    onError: onErro('Falha ao alterar o redirect para localhost.'),
  })

  const deleteMutation = useMutation({
    mutationFn: (name: string) => ldapApi.deleteOAuth2(name),
    onSuccess: (_data, name) => {
      qc.setQueryData<ListaOAuth2 | undefined>(['ldap-oauth2'], (old) => {
        if (!old) return old
        const items = old.items.filter((i) => i.name !== name)
        return { ...old, items, count: items.length }
      })
      if (detailName === name) closeDetail()
      closeDelete()
      notifications.show({ color: 'red', title: 'Cliente removido', message: '' })
    },
    onError: onErro('Falha ao apagar cliente.'),
  })

  async function handleRevelar(name: string) {
    setRevelarNome(name)
    setSegredo(null)
    setSegredoVisivel(false)
    setRevelarAberto(true)
    try {
      const valor = await ldapApi.revealOAuth2Secret(name)
      setSegredo(valor)
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status
      closeRevelar()
      if (status === 503) {
        notifications.show({
          color: 'red',
          title: 'Segredo não revelado',
          message: 'A trilha de auditoria está indisponível; o segredo não foi revelado.',
        })
      } else {
        notifications.show({ color: 'red', title: 'Erro', message: formatApiError(err, 'Falha ao revelar segredo.') })
      }
    }
  }

  function handleRemoverRedirect(rs: LdapOAuth2Client, url: string) {
    if (rs.redirects.length === 1) {
      setConfirmarUltimoRedirect({ name: rs.name, url })
      return
    }
    removeRedirectMutation.mutate({ name: rs.name, url })
  }

  if (isLoading) return <Skeleton height={200} />
  if (error) return <Alert color="red" icon={<IconAlertTriangle />}>Falha ao carregar clientes OAuth2 do Kanidm.</Alert>

  const clientes = data?.items ?? []
  const grupoOptions = (groupsData?.items ?? []).map((g) => ({ value: g.name, label: g.name }))
  const detalhe = clientes.find((c) => c.name === detailName) ?? null

  return (
    <>
      <Group justify="flex-end" mb="sm">
        <Button leftSection={<IconPlus size={14} />} size="xs" onClick={openCreate}>
          Novo Cliente
        </Button>
      </Group>

      <ScrollArea>
        <Table striped highlightOnHover withTableBorder fz="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Nome</Table.Th>
              <Table.Th>Exibição</Table.Th>
              <Table.Th>Tipo</Table.Th>
              <Table.Th>Redirects</Table.Th>
              <Table.Th>Scope map</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Ações</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {clientes.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={7}>
                  <Text c="dimmed" size="sm">Nenhum cliente OAuth2 encontrado.</Text>
                </Table.Td>
              </Table.Tr>
            )}
            {clientes.map((rs) => (
              <Table.Tr key={rs.name}>
                <Table.Td>
                  <Text
                    size="sm"
                    fw={500}
                    c="inovareTeal"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setDetailName(rs.name)}
                  >
                    {rs.name}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" c="dimmed">{rs.displayname}</Text>
                </Table.Td>
                <Table.Td>
                  <Badge size="xs" variant={rs.publico ? 'light' : 'outline'}>
                    {rs.publico ? 'público' : 'confidencial'}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Badge size="xs" variant="outline">{rs.redirects.length}</Badge>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" c="dimmed">
                    {rs.scopemaps.length > 0 ? rs.scopemaps.map((sm) => sm.group).join(', ') : '—'}
                  </Text>
                </Table.Td>
                <Table.Td>
                  {rs.protegido && <Badge size="xs" color="yellow">Usado por outra aplicação</Badge>}
                </Table.Td>
                <Table.Td>
                  <Group gap={4}>
                    {/* Público não tem segredo: a API responde 422 (spec §7.4). */}
                    {!rs.publico && (
                      <Tooltip label="Revelar segredo">
                        <ActionIcon
                          aria-label="Revelar segredo"
                          size="xs"
                          variant="subtle"
                          color="inovareTeal"
                          onClick={() => handleRevelar(rs.name)}
                        >
                          <IconKey size={13} />
                        </ActionIcon>
                      </Tooltip>
                    )}
                    <Tooltip label="Apagar cliente">
                      <ActionIcon
                        aria-label="Apagar"
                        size="xs"
                        variant="subtle"
                        color="red"
                        disabled={rs.protegido}
                        onClick={() => setDeleteAlvo(rs)}
                      >
                        <IconTrash size={13} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </ScrollArea>

      {/* Modal: Novo cliente */}
      <Modal
        opened={createOpened} onClose={closeCreate} title="Novo Cliente OAuth2" size="sm"
        transitionProps={{ duration: 0 }}
      >
        <Stack gap="sm">
          <Stack gap={4}>
            <Text size="sm" fw={500}>Tipo</Text>
            <SegmentedControl
              value={newTipo}
              onChange={(v) => setNewTipo(v as TipoCliente)}
              data={[
                { label: 'Confidencial', value: 'confidencial' },
                { label: 'Público', value: 'publico' },
              ]}
            />
          </Stack>
          <TextInput
            label="Nome"
            placeholder="kanbania"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
          <TextInput
            label="Nome de exibição"
            placeholder="KanbanIA"
            value={newDisplayname}
            onChange={(e) => setNewDisplayname(e.target.value)}
          />
          <TextInput
            label="Landing URL"
            placeholder="https://kanbania.example/"
            value={newLanding}
            onChange={(e) => setNewLanding(e.target.value)}
          />
          <Textarea
            label="Redirects (um por linha)"
            placeholder="https://kanbania.example/oidc/retorno"
            value={newRedirects}
            onChange={(e) => setNewRedirects(e.target.value)}
            autosize
            minRows={2}
          />
          {newTipo === 'publico' && (
            <Switch
              label="Permitir redirect para localhost (qualquer porta)"
              description="Para agentes locais com PKCE (ex.: Claude Code). Aceita qualquer porta e caminho em localhost, 127.0.0.0/8 e ::1."
              checked={newLocalhost}
              onChange={(e) => setNewLocalhost(e.currentTarget.checked)}
            />
          )}
          <Group justify="flex-end">
            <Button variant="subtle" onClick={closeCreate}>Cancelar</Button>
            <Button
              onClick={() => createMutation.mutate()}
              loading={createMutation.isPending}
              disabled={!newName || !newDisplayname || !newLanding}
            >
              Criar
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Modal: Detalhe do cliente */}
      <Modal
        opened={!!detalhe} onClose={closeDetail} title={detalhe?.name ?? ''} size="lg"
        transitionProps={{ duration: 0 }}
      >
        {detalhe && (
          <Stack gap="sm">
            {detalhe.protegido && (
              <Alert color="yellow" icon={<IconAlertTriangle size={14} />}>
                Cliente protegido (LDAP_OAUTH2_PROTEGIDOS): excluir, remover redirect e mexer
                no scope map ficam bloqueados
                {detalhe.publico ? ', e desligar o redirect para localhost também' : ''}.
              </Alert>
            )}
            <Text size="sm"><Text span fw={500}>Landing:</Text> {detalhe.landing_url}</Text>
            <Text size="sm">
              <Text span fw={500}>Tipo:</Text> {detalhe.publico ? 'público (PKCE, sem segredo)' : 'confidencial'}
            </Text>

            {detalhe.publico && (
              <Tooltip
                label="cliente protegido: desligar trancaria a aplicação"
                disabled={!(detalhe.protegido && detalhe.localhost_redirect)}
              >
                <div>
                  <Switch
                    label="Permitir redirect para localhost"
                    checked={detalhe.localhost_redirect}
                    disabled={
                      (detalhe.protegido && detalhe.localhost_redirect) || localhostMutation.isPending
                    }
                    onChange={(e) => localhostMutation.mutate({
                      name: detalhe.name, enabled: e.currentTarget.checked,
                    })}
                  />
                </div>
              </Tooltip>
            )}

            <Text size="sm" fw={500} mt="xs">Redirects</Text>
            {detalhe.redirects.length === 0 && <Text c="dimmed" size="sm">Nenhum redirect.</Text>}
            {detalhe.redirects.map((url) => (
              <Group key={url} justify="space-between" wrap="nowrap">
                <Text size="sm" style={{ wordBreak: 'break-all' }}>{url}</Text>
                {!detalhe.protegido && (
                  <Tooltip label="Remover redirect">
                    <ActionIcon
                      aria-label={`Remover redirect ${url}`}
                      size="xs"
                      variant="subtle"
                      color="red"
                      onClick={() => handleRemoverRedirect(detalhe, url)}
                    >
                      <IconTrash size={12} />
                    </ActionIcon>
                  </Tooltip>
                )}
              </Group>
            ))}

            {confirmarUltimoRedirect && confirmarUltimoRedirect.name === detalhe.name && (
              <Alert color="red" icon={<IconAlertTriangle size={14} />}>
                <Text size="sm">
                  Este é o último redirect: a aplicação deixa de conseguir login por ele.
                </Text>
                <Group justify="flex-end" mt="xs">
                  <Button size="xs" variant="subtle" onClick={() => setConfirmarUltimoRedirect(null)}>
                    Cancelar
                  </Button>
                  <Button
                    size="xs"
                    color="red"
                    loading={removeRedirectMutation.isPending}
                    onClick={() => {
                      removeRedirectMutation.mutate(confirmarUltimoRedirect)
                      setConfirmarUltimoRedirect(null)
                    }}
                  >
                    Remover mesmo assim
                  </Button>
                </Group>
              </Alert>
            )}

            <Group align="flex-end" gap="xs">
              <TextInput
                label="Nova URL de redirect"
                size="xs"
                style={{ flex: 1 }}
                value={novoRedirect}
                onChange={(e) => setNovoRedirect(e.target.value)}
              />
              <Button
                size="xs"
                disabled={!novoRedirect}
                loading={addRedirectMutation.isPending}
                onClick={() => addRedirectMutation.mutate({ name: detalhe.name, url: novoRedirect })}
              >
                Adicionar redirect
              </Button>
            </Group>
            {!detalhe.protegido && detalhe.publico && detalhe.localhost_redirect && (
              <Text size="xs" c="dimmed">
                Com a flag ligada, redirect de localhost não precisa ser cadastrado.
              </Text>
            )}

            <Text size="sm" fw={500} mt="xs">Scope maps</Text>
            {detalhe.scopemaps.length === 0 && <Text c="dimmed" size="sm">Nenhum scope map.</Text>}
            {detalhe.scopemaps.map((sm) => (
              <Group key={sm.group} justify="space-between" align="flex-end" wrap="nowrap">
                <Stack gap={2} style={{ flex: 1 }}>
                  <Text size="sm">{sm.group}</Text>
                  <Group gap={4} align="center" wrap="nowrap">
                    {/* openid é obrigatório (validar_escopos): pill fixa, fora do MultiSelect
                        para não poder ser removida por engano. */}
                    <Badge size="xs" variant="light" color="inovareTeal">openid</Badge>
                    <MultiSelect
                      size="xs"
                      style={{ flex: 1 }}
                      data={ESCOPOS_OPTIONS_SEM_OPENID}
                      value={sm.scopes.filter((s) => s !== 'openid')}
                      disabled={detalhe.protegido}
                      onChange={(escopos) => {
                        setScopeMapMutation.mutate({
                          name: detalhe.name, group: sm.group, scopes: [...escopos, 'openid'],
                        })
                      }}
                    />
                  </Group>
                </Stack>
                {!detalhe.protegido && (
                  <Button
                    size="xs"
                    color="red"
                    variant="subtle"
                    onClick={() => removeScopeMapMutation.mutate({ name: detalhe.name, group: sm.group })}
                  >
                    Remover scope map
                  </Button>
                )}
              </Group>
            ))}

            {!detalhe.protegido && (
              <Group align="flex-end" gap="xs">
                <Select
                  label="Grupo"
                  size="xs"
                  data={grupoOptions}
                  value={novoScopeMapGrupo}
                  onChange={setNovoScopeMapGrupo}
                  searchable
                  style={{ flex: 1 }}
                />
                <Stack gap={2} style={{ flex: 1 }}>
                  <Group gap={4} align="center">
                    <Text size="xs" fw={500}>Escopos</Text>
                    <Badge size="xs" variant="light" color="inovareTeal">openid</Badge>
                  </Group>
                  <MultiSelect
                    size="xs"
                    data={ESCOPOS_OPTIONS_SEM_OPENID}
                    value={novoScopeMapEscopos.filter((s) => s !== 'openid')}
                    onChange={(v) => setNovoScopeMapEscopos([...v, 'openid'])}
                  />
                </Stack>
                <Button
                  size="xs"
                  disabled={!novoScopeMapGrupo}
                  loading={setScopeMapMutation.isPending}
                  onClick={() => {
                    if (!novoScopeMapGrupo) return
                    setScopeMapMutation.mutate({
                      name: detalhe.name, group: novoScopeMapGrupo, scopes: novoScopeMapEscopos,
                    })
                  }}
                >
                  Definir scope map
                </Button>
              </Group>
            )}
          </Stack>
        )}
      </Modal>

      {/* Modal: Apagar */}
      <Modal
        opened={!!deleteAlvo} onClose={closeDelete} title={`Apagar ${deleteAlvo?.name ?? ''}`} size="sm"
        transitionProps={{ duration: 0 }}
      >
        {deleteAlvo && (
          <Stack gap="sm">
            <Text size="sm">
              Esta ação apaga o cliente OAuth2 <Text span fw={500}>{deleteAlvo.name}</Text> no
              Kanidm. Aplicações que usam este cliente perdem o login.
            </Text>
            <TextInput
              label={`Digite o nome do cliente para confirmar (${deleteAlvo.name})`}
              value={deleteValor}
              onChange={(e) => setDeleteValor(e.target.value)}
            />
            <Group justify="flex-end">
              <Button variant="subtle" onClick={closeDelete}>Cancelar</Button>
              <Button
                color="red"
                disabled={deleteValor !== deleteAlvo.name}
                loading={deleteMutation.isPending}
                onClick={() => deleteMutation.mutate(deleteAlvo.name)}
              >
                Confirmar exclusão
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* Modal: Revelar segredo */}
      <Modal
        opened={revelarAberto} onClose={closeRevelar} title={`Segredo de ${revelarNome ?? ''}`} size="sm"
        transitionProps={{ duration: 0 }}
      >
        <Stack gap="sm">
          <Code block style={{ wordBreak: 'break-all' }}>
            {segredoVisivel && segredo ? segredo : '•'.repeat(24)}
          </Code>
          <Group justify="flex-end">
            <Button
              size="xs"
              variant="subtle"
              leftSection={<IconEye size={14} />}
              onClick={() => setSegredoVisivel(true)}
            >
              Mostrar
            </Button>
            <Button
              size="xs"
              variant="subtle"
              leftSection={<IconCopy size={14} />}
              onClick={() => segredo && navigator.clipboard.writeText(segredo)}
            >
              Copiar
            </Button>
            <Button size="xs" onClick={closeRevelar}>Fechar</Button>
          </Group>
        </Stack>
      </Modal>
    </>
  )
}
